import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {actions, selectors} from '../Redux/index';
import moment from 'moment';
import {i18n, i18nString, formatTableDataForExport, formatDate} from '../Utility/Format';

import SettlementTable from '../Components/Tables/SettlementTable';
import LastUpdateMessage from '../Components/Widgets/LastUpdateMessage';
import {ValueDateCalendarToggle} from './Application/ValueDateCalendar';
import TableExport, {AverageRatesExport, MonthlyExport} from '../Components/Widgets/TableExport';
import StageActions from '../Components/Widgets/StageActions';

/**
 * The Settlement view container. One of the top-level views of the application.
 */
const Settlement = (props) => {

    const fixingsDate = props.table && props.table.fixingsDate ? moment(props.table.fixingsDate).format(props.locale === 'de' ? 'DD.MM.YYYY' : 'YYYY-MM-DD') : false;
    const fixingsTime = props.table && props.table.type ?
        (
            // format as fixing type as '13 Uhr' in german, otherwise '1 pm'
            moment()
                .hour(props.table.type === "FIRST" ? 13 : 18)
                .format(props.locale === "de" ? 'HH' : 'ha')
        ) : false;
    const footerMessage = i18nString('dbm.settlements.tableFooter', props.locale, {
        date: fixingsDate,
        time: fixingsTime
    });

    //Currency CNY replaced with CNH
    const currencyPairsWithFootnotes = [
        'EUR_CNH'
    ];

    const createTableDownloadHandler = (type) => {
        return () => {
            const data = formatTableDataForExport({
                key: "settlement",
                title: footerMessage,
                data: props.table,
                lang: props.locale
            });

            const options = {settlement: true};

            props.downloadFile(type, data, options);
        }
    };

    const createAverageDownloadHandler = (type) => {
        return (year) => {
            const data = {
                language: props.locale,
                title: i18nString('dbm.table.export.yearlyAvgTitle', props.locale, {year: year})
            };
            const options = {
                average: true,
                year: year
            }
            props.downloadFile(type, data, options);
        }
    };

    const createMonthlyDownloadHandler = (type) => {
        return (year, month) => {
            const values = {year: year, month: moment(month, "M").format("MMMM")};

            const data = {
                language: props.locale,
                title: i18nString('dbm.table.export.monthlyOverview', props.locale, values)          };
            const options = {
                monthly: true,
                year: year,
                month: month
            }
            props.downloadFile(type, data, options);
        }
    };

    const createMonthlyUltimoDownloadHandler = (type) => {
        return (year, month) => {
            const values = {year: year, month: moment(month, "M").format("MMMM")};

            const data = {
                language: props.locale,
                title: i18nString('dbm.table.export.monthlyUltimo', props.locale, values)
            };
            const options = {
                monthlyUltimo: true,
                year: year,
                month: month
            }
            props.downloadFile(type, data, options);
        }
    };

    const createEndOfMonthDownloadHandler = (type) => {
        return (year, month) => {
            const values = {year: year, month: moment(month, "M").format("MMMM")};

            const data = {
                language: props.locale,
                title: i18nString('dbm.table.export.EndofMonthRates', props.locale, values)
            };
            const options = {
                EndofMonthRates: true,
                year: year,
                month: month
            }
            props.downloadFile(type, data, options);
        }
    };

    const isEmpty = !props.table.data || props.table.data.length === 0;

    return (
        <div className="settlement">

            {
                isEmpty ? <center>{i18n(props.table.data ? 'dbm.table.emptyData' : 'dbm.table.loadingData')}</center> : (
                    <div>
                        <SettlementTable rows={props.table.data} currencyPairsWithFootnotes={currencyPairsWithFootnotes} locale={props.locale}/>
                        <LastUpdateMessage message={footerMessage} hint={i18n('dbm.settlements.options.fixingsAtFootnote', props.locale)}/>
                         <StageActions
                            left={
                                <TableExport
                                    label={i18n('dbm.stage.nav.settlement')}
                                    onClickPDF={createTableDownloadHandler('pdf')} 
                                    onClickCSV={createTableDownloadHandler('csv')} 
                                    onClickXLSX={createTableDownloadHandler('xlsx')}
                                    //aria-label={i18n('dbm.stage.nav.settlement')}
                                />
                            }
                            right={
                                <ValueDateCalendarToggle onTogglePanel={props.onToggleOptions} />
                            }
                        />
                    </div>
                )
            }

            <div className="stage-actions-group">
                <StageActions
                    full={
                        <AverageRatesExport
                            onClickPDF={createAverageDownloadHandler('pdf')}
                            onClickXLSX={createAverageDownloadHandler('xlsx')}
                            locale={props.locale}
                            //aria-label={i18nString('dbm.table.export.yearlyAvgTitle', props.locale, {year: ''})}
                        />
                    }
                />
                <StageActions
                    full={
                        <MonthlyExport
                            onClickPDF={createMonthlyDownloadHandler('pdf')}
                            onClickXLSX={createMonthlyDownloadHandler('xlsx')}
                            locale={props.locale}
                            label={i18nString('dbm.export.monthlyOverview', props.locale)}
                            //aria-label={i18nString('dbm.export.monthlyOverview', props.locale)}
                        />
                    }
                />
                <StageActions
                    full={
                        <MonthlyExport
                            onClickPDF={createMonthlyUltimoDownloadHandler('pdf')}
                            onClickXLSX={createMonthlyUltimoDownloadHandler('xlsx')}
                            locale={props.locale}
                            label={i18nString('dbm.export.monthlyUltimo', props.locale)}
                            //aria-label={i18nString('dbm.export.monthlyUltimo', props.locale)} 
                        />
                    }
                />
                <StageActions
                    full={
                        <MonthlyExport
                            onClickPDF={createEndOfMonthDownloadHandler('pdf')}
                            onClickXLSX={createEndOfMonthDownloadHandler('xlsx')}
                            locale={props.locale}
                            label={i18nString('dbm.export.EndofMonthRates', props.locale)}
                            //aria-label={i18nString('dbm.export.EndofMonthRates', props.locale)}
                        />
                    }
                />
            </div>

            <div className="stage__footnotes">
                <div className="stage__footnote">
                    <div className="stage__footnote-inner" dangerouslySetInnerHTML={{__html: i18nString('dbm.settlements.footnoteLeft', props.locale)}} />
                </div>

                <div className="stage__footnote">
                    <div className="stage__footnote-inner" dangerouslySetInnerHTML={{__html: i18nString('dbm.settlements.footnoteRight', props.locale)}} />
                </div>
            </div>
        </div>
    );
};

const mapDispatchToProps = (dispatch) => {
    return {
        onValuedateClick: () => {
            dispatch(actions.UI.OptionsMenu.toggleValueDateCalendar());
            dispatch(actions.UI.OptionsMenu.toggleOptionsMenu());

        },
        downloadFile: (type, data, options) => {
            dispatch(actions.Data.Download.requestDownloadFile({
                type: type,
                data: {body: JSON.stringify(data)},
                options: options
            }))
        }
    }
};

const mapStateToProps = (state) => {
    return {
        table: selectors.Data.Settlement.getData(state),
        locale: selectors.UI.Locale.getLocale(state),
        isValueDateVisible: selectors.UI.OptionsMenu.isValueDateVisible(state)
    };
};

Settlement.propTypes = {
    table: PropTypes.object.isRequired,
    locale: PropTypes.string
};

export default connect(mapStateToProps, mapDispatchToProps)(Settlement);